<template>
  <div :class="$options.name">
    <v-card>
      <v-toolbar color="blue darken-3" dark flat>
        <v-btn
          v-permission="'user-create'"
          :to="{ name: 'users-create' }"
          color="primary"
          class="elevation-0"
        >
          Создать нового пользователя
        </v-btn>
        <div class="flex-grow-1" />
        <v-text-field
          v-model="search"
          label="Поиск"
          prepend-inner-icon="search"
          flat
          clearable
          single-line
          hide-details
          @input="debounceHandleSearch"
        />

        <users-filter
          :items-list="roleList"
          :selected-items="selectedRoles"
          :is-loading="isLoading"
          @change="selectedRole"
        />
      </v-toolbar>
      <v-data-table
        :items="usersList"
        :loading="isLoading"
        :headers="headers"
        :items-per-page="pagination.perPage"
        item-key="id"
        hide-default-footer
      >
        <template #item.role="{ item }">
          {{ item.roles.map((role) => role.name).join(', ') }}
        </template>

        <template #item.actions="{ item }">
          <v-btn
            v-if="item.managed"
            v-permission="['user-edit']"
            :to="{ name: 'users-item', params: { id: item.id } }"
            color="light"
            x-small
          >
            <v-icon left>edit</v-icon> Редактировать
          </v-btn>

          <v-btn
            v-if="item.managed"
            v-permission="['user-delete']"
            color="error"
            class="ml-2"
            x-small
            @click="$dialog.show('user-remove-confirmation', { itemId: item.id })"
          >
            <v-icon left>delete</v-icon> Удалить
          </v-btn>
        </template>

        <template v-if="pagination" #footer>
          <table-footer
            :pagination="pagination"
            @onChangePage="onChangePage"
            @changePerPage="changePerPage"
          />
        </template>
      </v-data-table>
    </v-card>

    <remove-confirmation
      name="user-remove-confirmation"
      title="Вы действительно желаете удалить пользователя?"
      @confirmed="onUserRemove"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import RemoveConfirmation from '@/components/dialogs/confirmations/RemoveConfirmation.vue';
import debounce from 'lodash/debounce';
import UsersFilter from '@/components/page/users-filter';
import TableFooter from '@/components/page/table-footer';

import { getUsers, deleteUser } from '@/services/users';

export default {
  name: 'users-view-list',

  components: {
    RemoveConfirmation,
    UsersFilter,
    TableFooter,
  },

  data: () => ({
    isLoading: false,
    headers: [
      { value: 'name', text: 'Имя пользователя', sortable: false },
      { value: 'email', text: 'E-mail', sortable: false },
      { value: 'role', text: 'Роли', sortable: false },
      {
        value: 'actions',
        sortable: false,
        width: '30%',
        align: 'right',
      },
    ],
    search: '',
    selectedRoles: [],
    usersList: [],
    pagination: {},
  }),

  computed: {
    ...mapState('roles', { roleList: 'fullList' }),
  },

  watch: {
    // eslint-disable-next-line func-names
    '$route.query': {
      handler(data) {
        const { query, roleIds } = data;
        if (query) this.search = query;
        if (roleIds) {
          this.selectedRoles = typeof roleIds === 'string' ? [roleIds] : roleIds;
        }

        this.downloadUserList({ ...data, roleIds: this.selectedRoles });
      },
      deep: true,
      immediate: true,
    },
  },

  async created() {
    this.pagination.perPage = 12;
    this.debounceHandleSearch = debounce(this.handleSearch, 1000);
    await this.getFullList();
  },

  methods: {
    ...mapActions(['showNotify']),
    ...mapActions('roles', ['getFullList']),

    async downloadUserList(query) {
      this.isLoading = true;
      try {
        const { data, meta } = await getUsers(query);
        this.usersList = data;
        this.pagination = {
          ...meta,

          perPage: meta.per_page,
          lastPage: meta.last_page,
          currentPage: meta.current_page,
        };
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },

    selectedRole(roles) {
      this.selectedRoles = roles;
      this.handleSearch(this.search);
    },

    changePerPage(perPage) {
      this.pagination.perPage = perPage;
      this.handleSearch(this.search);
    },

    handleSearch(search) {
      this.pagination.currentPage = 1;
      this.$router.replace({
        path: this.$route.path,
        query: {
          // Логичнее было бы назвать query параметр search
          query: search,
          roleIds: this.selectedRoles,
          perPage: this.pagination.perPage,
          page: this.pagination.currentPage,
        },
      });
    },

    async onChangePage(page) {
      this.$router.replace({
        path: this.$route.path,
        query: {
          ...this.$route.query,
          page,
        },
      });
    },

    async onUserRemove(userId) {
      await deleteUser(userId);
      this.showNotify({ message: 'Пользователь успешно удален' });
      this.downloadUserList(this.$route.query);
    },
  },
};
</script>

<style lang="scss" scoped>
  .footer-container {
    display: flex;
    align-items: baseline;
    justify-content: center;

    &__autocomplete {
      padding: 15px;
    }
  }
</style>
